import memoize from 'memoize-one';
import moment from 'moment';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import DatePickerInput, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { enGB, fr } from 'date-fns/locale';
import { nsOptions } from '../i18n';
import { isMobileView } from './MobileView';
import { childrenPropTypes } from '../utils/generic-prop-types';
import {
  DATE_TIME, DATE_FULL, DATE_MONTH_YEAR, DATE_YEAR,
} from '../constants';

registerLocale('en', enGB);
registerLocale('fr', fr);

// Le pop-up est rattache au corps de la page plutot qu'au formulaire : la zone
// de defilement du formulaire (.inclusion-form-box) et le corps de la modale
// rognent tout ce qui depasse, et un pop-up "date + heure" ne tient ni
// au-dessus ni au-dessous du champ des que la fenetre est un peu courte.
const PopperPortal = ({ children }) => ReactDOM.createPortal(children, document.body);

PopperPortal.propTypes = {
  children: childrenPropTypes().isRequired,
};

export const getDateInputFormat = (t, format) => {
  switch (format) {
    case DATE_TIME:
      return t('common:elements.date-picker.date-time-format');
    case DATE_MONTH_YEAR:
      return t('common:elements.date-picker.date-month-year-format');
    case DATE_YEAR:
      return t('common:elements.date-picker.date-year-format');
    case DATE_FULL:
    default:
      return t('common:elements.date-picker.date-full-format');
  }
};

class CustomInput extends Component {
  onChange = (e) => {
    if (this.checkChars(e.target.value)) {
      this.props.onChange(e);
    }
  };

  checkChars = (str) => {
    const { format } = this.props;
    let allowedChars;

    switch (format) {
      case DATE_TIME:
        allowedChars = '0-9/: ';
        break;

      case DATE_YEAR:
        allowedChars = '0-9';
        break;

      case DATE_MONTH_YEAR:
      case DATE_FULL:
      default:
        allowedChars = '0-9/';
        break;
    }

    const regex = new RegExp(`^[${allowedChars}]*$`);
    return str.match(regex);
  };

  render() {
    const { onChange, format, ...remainingProps } = this.props;
    return (
      <input
        {...remainingProps}
        onChange={this.onChange}
        autoComplete="off"
      />
    );
  }
}

CustomInput.propTypes = {
  onChange: PropTypes.func,
  format: PropTypes.oneOf([
    DATE_TIME,
    DATE_FULL,
    DATE_MONTH_YEAR,
    DATE_YEAR,
  ]),
};

CustomInput.defaultProps = {
  onChange: () => {},
  format: DATE_FULL,
};


@withTranslation('', nsOptions)
class DatePicker extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    tReady: PropTypes.bool,
    i18n: PropTypes.shape().isRequired,
    id: PropTypes.string,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    defaultValue: PropTypes.shape(),
    minDate: PropTypes.shape(),
    maxDate: PropTypes.shape(),
    customInputClassName: PropTypes.string,
    isInclusion: PropTypes.bool,
    format: PropTypes.oneOf([
      DATE_TIME,
      DATE_FULL,
      DATE_MONTH_YEAR,
      DATE_YEAR,
    ]),
  };

  static defaultProps = {
    tReady: false,
    id: null,
    onChange: null,
    placeholder: null,
    disabled: false,
    defaultValue: null,
    minDate: null,
    maxDate: null,
    customInputClassName: null,
    isInclusion: false,
    format: DATE_FULL,
  };

  constructor(props) {
    super(props);
    let defaultValue = null;
    if (props.defaultValue) {
      defaultValue = moment(props.defaultValue).toDate();
    }
    this.state = { value: defaultValue };
    const momentToDate = (date) => (date ? date.toDate() : null);
    this.formatMinDate = memoize(momentToDate);
    this.formatMaxDate = memoize(momentToDate);
  }

  componentDidMount() {
    if (this.usesPortal()) {
      document.addEventListener('scroll', this.closeOnOutsideScroll, true);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.closeOnOutsideScroll, true);
  }

  // Rattache au corps de la page, le pop-up ne suit plus le champ quand le
  // formulaire defile : react-popper ne recalcule pas sa position ici. On le
  // referme donc, comme le font la plupart des menus flottants. Le defilement
  // interne de la liste des heures ne doit evidemment rien fermer.
  closeOnOutsideScroll = (event) => {
    const { target } = event;
    if (!this.calendar || !this.calendar.state || !this.calendar.state.open) return;
    if (target && target.closest && target.closest('.react-datepicker-popper')) return;
    this.calendar.setOpen(false);
  };

  usesPortal = () => this.props.format === DATE_TIME && !isMobileView();

  // react-datepicker fournit l'evenement d'origine en second argument pour une
  // saisie au clavier, et ne le fournit pas au clic sur un creneau horaire.
  // C'est ce qui permet de distinguer les deux sans rien memoriser.
  onChange = (jsDate, event) => {
    // Une date tapee sans son heure ("15/09/2026") est retenue pour le pop-up,
    // pour que le choix d'un creneau parte bien de cette date ; mais rien n'est
    // enregistre, car inventer minuit reviendrait a inscrire une heure que
    // personne n'a saisie.
    const typedText = event && event.target && typeof event.target.value === 'string'
      ? event.target.value
      : null;

    if (jsDate !== null && this.props.format === DATE_TIME
        && typedText !== null && !typedText.includes(':')) {
      this.setState({ value: jsDate });
      return;
    }

    if (jsDate === null) {
      // Date has been cleared
      this.setState({ value: null });
      this.props.onChange(null);
    } else {
      // A valid or invalid date has been entered
      const {
        minDate, maxDate, onChange, format,
      } = this.props;
      let momentDate;
      if (format === DATE_TIME) {
        momentDate = moment(jsDate);
      } else {
        // Replace default react-datepicker time by midnight utc
        momentDate = moment.utc({
          year: jsDate.getFullYear(),
          month: jsDate.getMonth(),
          date: jsDate.getDate(),
        });
      }
      if (momentDate.isValid()) {
        if (minDate && momentDate.isBefore(minDate)) return;
        if (maxDate && momentDate.isAfter(maxDate)) return;
        this.setState({ value: jsDate });
        if (onChange) onChange(momentDate.format());
      }
    }
  };

  render() {
    const {
      disabled, i18n, id, placeholder, minDate, maxDate, format, t, customInputClassName,
    } = this.props;
    const { value } = this.state;
    const options = {};

    switch (format) {
      case DATE_TIME:
        if (isMobileView()) {
          options.showTimeInput = true;
        } else {
          options.showTimeSelect = true;
        }
        options.timeIntervals = 15;
        options.timeFormat = t('common:elements.date-picker.time-select-format');
        options.timeCaption = t('common:elements.time-caption');
        break;

      case DATE_MONTH_YEAR:
        options.showMonthYearPicker = true;
        break;

      case DATE_YEAR:
        options.showYearPicker = true;
        break;

      case DATE_FULL:
      default:
        break;
    }

    options.dateFormat = getDateInputFormat(t, format);

    // Une date saisie sans son heure ("15/09/2026") doit deja etre reconnue.
    // Sinon react-datepicker n'a aucune date de reference et, au clic sur un
    // creneau horaire, il repart de la date du jour : la date saisie est
    // silencieusement remplacee par celle du jour.
    // parseDate accepte une liste de formats ; le premier sert a l'affichage.
    if (format === DATE_TIME) {
      options.dateFormat = [options.dateFormat, getDateInputFormat(t, DATE_FULL)];
    }

    const customInputProps = {};
    if (customInputClassName) {
      customInputProps.className = customInputClassName;
    }

    const { isInclusion } = this.props;
    const datepickerClassName = `react-datepicker-input${isInclusion ? ' inclusion-datepicker' : ''}`;

    if (this.usesPortal()) {
      // Sorti du formulaire, le pop-up n'est plus un descendant de
      // .entry-context : il porte donc lui-meme le marqueur qui lui vaut les
      // couleurs du contexte de saisie.
      options.popperClassName = `datepicker-with-time${isInclusion ? ' datepicker-inclusion' : ''}`;
      options.popperContainer = PopperPortal;
    }

    return (
      <DatePickerInput
        id={id}
        onChange={this.onChange}
        strictParsing
        selected={value}
        className={datepickerClassName}
        placeholderText={placeholder}
        disabled={disabled}
        minDate={this.formatMinDate(minDate)}
        maxDate={this.formatMaxDate(maxDate)}
        disabledKeyboardNavigation
        showYearDropdown
        scrollableYearDropdown
        dropdownMode="select" // Better support for very old and future years
        showMonthDropdown
        isClearable={!disabled}
        locale={i18n.language}
        customInput={<CustomInput format={format} {...customInputProps} />}
        ref={(calendar) => {
          this.calendar = calendar;
        }}
        {...options}
      />
    );
  }
}

export default DatePicker;
